// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import './styles.css';
import { loadDataset, searchNames, type Dataset } from './data';
import type { ViewId } from './types';
import type { ViewContext } from './viewContext';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossaryPopover';
import { createAbout } from './components/about';
import { createDrilldown } from './components/drilldown';
import { renderOverview } from './views/overview';
import { renderFinder } from './views/finder';
import { renderCharts } from './views/charts';
import { renderDiversity } from './views/diversity';
import { renderMovers } from './views/movers';
import { renderUnisex } from './views/unisex';
import { renderLetters } from './views/letters';
import { renderStates } from './views/states';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';
import { escapeHtml, formatDate } from './utils/format';

// Nav tabs are words only — never count badges.
const VIEWS: Array<{ id: ViewId; label: string; render: (root: HTMLElement, ctx: ViewContext) => void }> = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'finder', label: 'Name Finder', render: renderFinder },
  { id: 'charts', label: 'The Charts', render: renderCharts },
  { id: 'diversity', label: 'Diversity', render: renderDiversity },
  { id: 'movers', label: 'Risers & Fallers', render: renderMovers },
  { id: 'unisex', label: 'Unisex', render: renderUnisex },
  { id: 'letters', label: 'First Letters', render: renderLetters },
  { id: 'states', label: 'NSW vs QLD', render: renderStates },
  { id: 'explorer', label: 'Explorer', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
];

const LOGO = `<svg viewBox="0 0 32 32" aria-hidden="true">
  <rect x="1" y="1" width="30" height="30" rx="8" fill="#d1613f"/>
  <path d="M16 6.2l2.34 4.9 5.36.72-3.94 3.7 1.02 5.32L16 18.3l-4.78 2.54 1.02-5.32-3.94-3.7 5.36-.72z" fill="#fffdf8"/>
  <circle cx="16" cy="24.4" r="1.7" fill="#fffdf8"/>
</svg>`;

function parseHash(): { view: ViewId | null; name: string | null } {
  const h = location.hash.replace(/^#/, '');
  if (!h) return { view: null, name: null };
  if (h.startsWith('name=')) return { view: null, name: decodeURIComponent(h.slice('name='.length)) };
  const view = VIEWS.find((v) => v.id === h)?.id ?? null;
  return { view, name: null };
}

function shell(app: HTMLElement, data: Dataset): void {
  const { meta } = data;

  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#overview">${LOGO}<span>Baby Names</span><span class="brand-sub">Australia · 1952–${meta.latestYear}</span></a>
        <div class="search-wrap">
          <input class="search-input" type="search" placeholder="Type a name…" aria-label="Search a baby name" autocomplete="off" />
          <div class="search-results" role="listbox"></div>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" data-role="about" aria-label="About this site" title="About this site">?</button>
      </div>
    </header>
    <nav class="nav-tabs" aria-label="Views">
      <div class="nav-inner" role="tablist">
        ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
      </div>
    </nav>
    <main class="main-content" id="view-root"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>
          Data: <a href="${meta.sources.nsw}" target="_blank" rel="noopener">NSW BDM</a> (1952–${meta.coverage.NSW[1]}) &amp;
          <a href="${meta.sources.qld}" target="_blank" rel="noopener">QLD BDM</a> (1960–${meta.coverage.QLD[1]}),
          CC BY. Top-100 names only. Updated ${formatDate(meta.generated)}.
        </span>
        <span>
          Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> ·
          <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        </span>
      </div>
    </footer>
  `;

  const viewRoot = app.querySelector('#view-root') as HTMLElement;
  const about = createAbout(meta);
  const drill = createDrilldown(data);
  let current: ViewId = 'overview';

  const ctx: ViewContext = {
    data,
    openName: (n) => drill.open(n),
    goTo: (v) => show(v),
  };

  function show(id: ViewId, pushHash = true): void {
    const view = VIEWS.find((v) => v.id === id) ?? VIEWS[0];
    drill.close();
    current = view.id;
    app.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('data-view') === current)));
    viewRoot.innerHTML = '';
    try {
      view.render(viewRoot, ctx);
    } catch {
      viewRoot.innerHTML = '<div class="error-box">Something went wrong rendering this view. Try another tab.</div>';
    }
    if (pushHash) history.replaceState(null, '', `#${current}`);
    window.scrollTo({ top: 0 });
  }

  app.querySelector('.nav-inner')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-view]');
    if (btn) show(btn.getAttribute('data-view') as ViewId);
  });
  app.querySelector('[data-role="about"]')?.addEventListener('click', () => about.open());
  app.querySelector('.brand')?.addEventListener('click', (e) => {
    e.preventDefault();
    show('overview');
  });

  // ── Header search ──
  const input = app.querySelector('.search-input') as HTMLInputElement;
  const results = app.querySelector('.search-results') as HTMLElement;
  let hits: ReturnType<typeof searchNames> = [];
  let cursor = -1;
  const closeSearch = () => {
    results.innerHTML = '';
    cursor = -1;
  };
  const paintResults = () => {
    results.innerHTML = hits
      .map(
        (n, i) => `
        <button class="search-item" data-key="${escapeHtml(n.k)}" role="option" aria-selected="${i === cursor}">
          <strong>${escapeHtml(n.n)}</strong>
          <span>best #${n.bestRank} in ${n.bestYear}</span>
          <em>${n.totalCount.toLocaleString('en-AU')}</em>
        </button>`,
      )
      .join('');
  };
  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      hits = searchNames(data.names, input.value);
      cursor = -1;
      paintResults();
    }, 250);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      input.blur();
      return;
    }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      cursor = (cursor + 1) % hits.length;
      paintResults();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      cursor = (cursor - 1 + hits.length) % hits.length;
      paintResults();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = hits[cursor >= 0 ? cursor : 0];
      if (pick) {
        drill.open(pick.k);
        input.value = '';
        closeSearch();
      }
    }
  });
  results.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-key]');
    if (!btn) return;
    drill.open(btn.getAttribute('data-key') as string);
    input.value = '';
    closeSearch();
  });
  document.addEventListener('click', (e) => {
    if (!(e.target as Element).closest('.search-wrap')) closeSearch();
  });

  // ── Routing ──
  const initial = parseHash();
  show(initial.view ?? 'overview', !initial.name);
  if (initial.name) drill.open(initial.name);
  window.addEventListener('hashchange', () => {
    const { view, name } = parseHash();
    if (name) drill.open(name);
    else if (view && view !== current) show(view, false);
  });
}

function renderError(app: HTMLElement, retry: () => void): void {
  app.innerHTML = `
    <main class="main-content">
      <div class="error-box">
        <strong>Could not load the data.</strong>
        <p style="margin-top:8px">Check your connection and try again.</p>
        <button type="button">Retry</button>
      </div>
    </main>`;
  app.querySelector('button')?.addEventListener('click', retry);
}

async function boot(): Promise<void> {
  const app = document.getElementById('app') as HTMLElement;
  app.innerHTML = `
    <main class="main-content">
      <div class="loading">Loading 74 years of baby names…</div>
      <div class="skeleton"></div>
    </main>`;
  initTooltip();
  initGlossary();
  try {
    const data = await loadDataset();
    shell(app, data);
  } catch {
    renderError(app, () => {
      void boot();
    });
  }
}

void boot();

export type { Dataset };
